import API_URL from '../config/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('armita_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Axial MRI Image cache service
class AxialImageCacheService {
  constructor() {
    this.API_URL = API_URL;
    this.cache = new Map();
    this.axialInfo = null;
    this.ivdMapping = null;
    this.pendingRequests = new Map();
    this.requestQueue = [];
    this.activeRequests = 0;
    this.maxConcurrentRequests = 4;
  }

  // Load axial info and IVD mapping
  async loadAxialInfo() {
    try {
      const response = await fetch(`${this.API_URL}/mri/axial/info`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to load axial MRI info');
      }
      this.axialInfo = await response.json();

      // Also load IVD mapping
      const ivdResponse = await fetch(`${this.API_URL}/mri/ivd-levels`, { headers: getAuthHeaders() });
      if (ivdResponse.ok) {
        const ivdData = await ivdResponse.json();
        this.ivdMapping = ivdData.ivd_mapping || {};
      }

      return {
        axialInfo: this.axialInfo,
        ivdMapping: this.ivdMapping
      };
    } catch (err) {
      console.error('Error loading axial MRI info:', err);
      throw err;
    }
  }

  // Process the request queue
  async processQueue() {
    while (this.requestQueue.length > 0 && this.activeRequests < this.maxConcurrentRequests) {
      const request = this.requestQueue.shift();
      this.activeRequests++;

      request().finally(() => {
        this.activeRequests--;
        this.processQueue();
      });
    }
  }

  // Fetch a single slice with caching
  async fetchSlice(sliceIndex, withSegmentation = true) {
    const cacheKey = `ax_${sliceIndex}_${withSegmentation}`;

    // Check cache first
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Check if request is already pending
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Create the fetch promise
    const fetchPromise = new Promise((resolve, reject) => {
      const doFetch = async () => {
        try {
          const response = await fetch(
            `${this.API_URL}/mri/axial/slice/${sliceIndex}?with_segmentation=${withSegmentation}`,
            { headers: getAuthHeaders() }
          );

          if (!response.ok) {
            throw new Error(`Failed to fetch axial slice: ${response.statusText}`);
          }

          const data = await response.json();

          // Cache the processed result
          const cacheData = {
            imageUrl: `data:image/png;base64,${data.image_data}`,
            dimensions: data.dimensions,
            hasSegmentation: data.has_segmentation,
            ivdLevels: data.ivd_levels || [],
            totalSlices: data.total_slices
          };

          this.cache.set(cacheKey, cacheData);

          // Remove from pending
          this.pendingRequests.delete(cacheKey);

          resolve(cacheData);
        } catch (err) {
          this.pendingRequests.delete(cacheKey);
          reject(err);
        }
      };

      // Add to queue
      this.requestQueue.push(doFetch);
      this.processQueue();
    });

    this.pendingRequests.set(cacheKey, fetchPromise);
    return fetchPromise;
  }

  // Get cached slice if available
  getSlice(sliceIndex, withSegmentation = true) {
    const cacheKey = `ax_${sliceIndex}_${withSegmentation}`;
    return this.cache.get(cacheKey);
  }

  // Preload slices around a given index
  async preloadSlices(centerIndex, totalSlices, range = 5) {
    const startIndex = Math.max(0, centerIndex - range);
    const endIndex = Math.min(totalSlices - 1, centerIndex + range);

    for (let i = startIndex; i <= endIndex; i++) {
      if (i !== centerIndex) {
        // Fire and forget
        this.fetchSlice(i, true).catch(() => { });
      }
    }
  }

  // Preload all IVD-mapped slices (these are the important ones)
  async preloadIvdSlices(onProgress) {
    if (!this.ivdMapping) {
      await this.loadAxialInfo();
    }

    const ivdSlices = Object.values(this.ivdMapping || {})
      .map(info => info.axial_slice)
      .filter((v, i, a) => a.indexOf(v) === i); // unique

    let loaded = 0;
    const total = ivdSlices.length;

    for (const sliceIndex of ivdSlices) {
      await this.fetchSlice(sliceIndex, true);
      loaded++;
      if (onProgress) {
        onProgress((loaded / total) * 100);
      }
    }
  }

  // Preload all available slices
  async preloadAllSlices(onProgress) {
    if (!this.axialInfo) {
      await this.loadAxialInfo();
    }

    // Get all available slices from info
    const slices = this.axialInfo?.available_slices || [];
    if (slices.length === 0) return;

    console.log(`Preloading all ${slices.length} axial slices...`);

    let loaded = 0;
    const total = slices.length;

    // Process in chunks to avoid overwhelming the browser/network
    const chunkSize = 5;
    for (let i = 0; i < total; i += chunkSize) {
      const chunk = slices.slice(i, i + chunkSize);
      await Promise.all(chunk.map(sliceIdx => this.fetchSlice(sliceIdx, true).catch(e => console.warn(e))));

      loaded += chunk.length;
      if (onProgress) onProgress(Math.min(100, (loaded / total) * 100));
    }
  }

  // Clear cache
  clear() {
    this.cache.clear();
    this.pendingRequests.clear();
    this.requestQueue = [];
    this.axialInfo = null;
    this.ivdMapping = null;
  }

  // Get slice index for an IVD level
  getSliceForIvdLevel(level) {
    if (!this.ivdMapping) return null;

    // Convert level format: "L4-L5" -> "IVD_L4/L5"
    const ivdKey = `IVD_${level.replace('-', '/')}`;
    const info = this.ivdMapping[ivdKey];

    return info ? info.axial_slice : null;
  }

  // Get IVD levels for a slice
  getIvdLevelsForSlice(sliceIndex) {
    if (!this.ivdMapping) return [];

    return Object.entries(this.ivdMapping)
      .filter(([_, info]) => info.axial_slice === sliceIndex)
      .map(([ivdName, _]) => ivdName);
  }
}

// Create singleton instance
const axialImageCache = new AxialImageCacheService();
export default axialImageCache;
